<template>
  <div class="relative text-shadow-none">
    <div class="bg-butterfly-300 text-butterfly-800 dark:bg-butterfly-800 dark:text-butterfly-100 py-1.5 px-2 space-x-0.5 rounded-full h-6 inline-flex items-center cursor-pointer" @click="onMenu">
      <IconBase name="cute/translation" :class="[ isTranslating ? 'animate-pulse' : '' ]" :width="18" class="!-ml-0.5" />
      <div class="grow text-2xs font-bold !leading-none" :class="[ isTranslating ? 'animate-pulse' : '' ]">
        <template v-if="isTranslating">
          {{ $t('components.Translate.translating') }}
        </template>
        <template v-else-if="isTranslated">
          {{ $t('components.Translate.translated') }}
        </template>
        <template v-else-if="isTranslatable">
          {{ $t('components.Translate.translate') }}
        </template>
      </div>

      <IconBase name="cute/spinner" class="spin !-mr-1 !ml-1.5" :width="18" v-if="isLoading" />
      <IconBase name="cute/angle-right" class="rotate-90 !-mr-0.5" :width="18" v-else-if="isTranslated" />
    </div>
    
    <div class="absolute mt-2 rounded-md z-[199] left-0 min-w-52 bg-butterfly-300 text-butterfly-800 dark:bg-butterfly-800 dark:text-butterfly-100 text-sm p-1" ref="$menu" v-if="isMenu">
      <p class="py-2 px-2 cursor-pointer hover:bg-butterfly-700 rounded" v-for="item in nav" @click="item.action">
        {{ item.label }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import EventsStore from '@/store/events'
import PostsStore from '@/store/posts'

import { translationsGetAPI } from '~/api/translations'
import { Event, Post, User } from '~/classes'
import { onClickOutside } from '@vueuse/core'

const props = withDefaults(defineProps<{
  user?: User
  event?: Event
  post?: Post
}>(), {
  user: undefined,
  event: undefined,
  post: undefined
})

const $menu = ref()
onClickOutside($menu, () => isMenu.value = false)

const postsStore = PostsStore()
const eventsStore = EventsStore()
const userStore = UserStore()
const { currentUser, langExcluded } = storeToRefs(userStore)
const usersStore = UsersStore()

const isLoading = ref(false)
const isTranslating = ref(false)
const isMenu = ref(false)

const router = useRouter()

const langOriginal = computed(() => {
  if (props.user) {
    return props.user.lang || 'unknown'
  } else if (props.event) {
    return props.event.lang || 'unknown'
  } else if (props.post) {
    return props.post.lang || 'unknown'
  }

  return 'unknown'
})

const nav = computed(() => {

  return [
    {
      label: useNuxtApp().$i18n.t('components.Translate.original', {
        lang: (langOriginal.value !== 'unknown' ? languageGetName(langOriginal.value, useNuxtApp().$i18n.locale.value) : useNuxtApp().$i18n.t('common.unknown')).toLowerCase()
      }),
      action: async () => {
        isMenu.value = false

        langExcluded.value = [
          ...langExcluded.value,
          ...(props.user?._id ? [props.user._id] : []),
          ...(props.event?._id ? [props.event._id] : []),
          ...(props.post?._id ? [props.post._id] : [])
        ]
      },
      isDisabled: !isTranslated.value
    },
    {
      label: useNuxtApp().$i18n.t('components.Translate.exclude'),
      action: async () => {
        if (!currentUser.value) return
        isMenu.value = false
        isLoading.value = true

        await userStore.edit({
          langExcluded: [
            ...(currentUser.value.langExcluded ?? []),
            langOriginal.value
          ]
        }, { silent: true })

        isLoading.value = false
      },
      isDisabled: !isTranslated.value || (currentUser.value?.langExcluded ?? []).includes(langOriginal.value) || langOriginal.value === 'unknown'
    },
    {
      label: useNuxtApp().$i18n.t('components.Translate.menu'),
      action: async () => {
        router.push({ name: 'app-params' })
      }
    }
  ].filter(i => !i.isDisabled)
})

const onMenu = () => {
  if (isTranslated.value) {
    isMenu.value = !isMenu.value
  } else {
    onTranslate()

    useNuxtApp().$amplitude.logEvent(`Translate_start`, {
      userId: props.user?._id,
      eventId: props.event?._id,
      postId: props.post?._id,
    })
  }
}

const hasTranslates = computed(() => {
  if (props.user) return props.user.translations && Object.keys(props.user.translations).length > 0
  if (props.event) return props.event.translations && Object.keys(props.event.translations).length > 0
  if (props.post) return props.post.translations && Object.keys(props.post.translations).length > 0

  return false
})

const isTranslatable = computed(() => {
  return langOriginal.value !== currentUser.value?.langTarget
})

const isTranslated = computed(() => {
  return currentUser.value?.isLangTranslate && isTranslatable.value && hasTranslates.value && !(currentUser.value?.langExcluded ?? []).includes(langOriginal.value) && !langExcluded.value.includes(props.user?._id ?? props?.event?._id ?? props.post?._id ?? '')
})

const onTranslate = async () => {
  if (!currentUser.value) return

  isTranslating.value = true
  isMenu.value = false

  if (!currentUser.value.isLangTranslate || (currentUser.value.langExcluded ?? []).includes(langOriginal.value)) {
    await userStore.edit({
      isLangTranslate: true,
      langExcluded: currentUser.value.langExcluded?.filter((lang) => lang !== langOriginal.value),
    }, { silent: true })
  }

  langExcluded.value = langExcluded.value.filter(id => id !== props.user?._id && id !== props.event?._id && id !== props.post?._id)

  if (!hasTranslates.value) {
    const response = await translationsGetAPI(currentUser.value?.langTarget ?? 'en', {
      userId: props.user?._id,
      eventId: props.event?._id,
      postId: props.post?._id
    })

    if (response.ok && response.data.translations) {

      if (props.user) {
        usersStore.updateItem(props.user._id, {
          translations: response.data.translations
        })
      } else if (props.event) {
        eventsStore.updateItem(props.event._id, {
          translations: response.data.translations
        })
      } else if (props.post) {
        postsStore.updateItem(props.post._id, {
          translations: response.data.translations
        })
      }
    }
  }

  isTranslating.value = false
}

</script>